import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useMemo } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { withTypes } from "react-final-form";
import { useQueryClient } from "react-query";
import { SkuRelationshipRequest } from "services/KnowfullyAdminClient";
import { SkuFormProps } from "types/sku-related-types";
import translateError from "utils/translateError";
import SelectSkuRelationship from "./SelectSkuRelationshipType";
import SelectSku from "components/SelectSku";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

type SkuRelationshipFormProps = {
  onCancel?: () => void;
  onSkuRelationshipCreated: () => void;
};

type SkuRelationshipTypeFormValues = {
  parentSkuId: string;
  childSkuId: string;
  skuRelationshipTypeId: string;
};

const { Form } = withTypes<SkuRelationshipTypeFormValues>();

const SkuRelationshipForm = ({
  sku,
  onCancel,
  onSkuRelationshipCreated,
  onSkuUpdated,
  ...rest
}: SkuRelationshipFormProps & SkuFormProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { registerPromise } = useTrashablePromises();
  const { skuService } = useGlobalContext();
  const queryClient = useQueryClient();

  const init = useMemo<SkuRelationshipTypeFormValues>(
    () => ({
      parentSkuId: sku?.id!,
      childSkuId: "",
      skuRelationshipTypeId: "",
    }),
    [sku],
  );

  return useMemo(() => {
    const validate = (values: SkuRelationshipTypeFormValues) => {
      const errors: Partial<
        Record<keyof SkuRelationshipTypeFormValues, string>
      > = {};

      if (values.childSkuId === values.parentSkuId) {
        errors.childSkuId = t({ id: "skuRelationshipSelfReferenceError" });
      }

      return errors;
    };

    const onSubmit = async (values: SkuRelationshipTypeFormValues) => {
      try {
        const response = await registerPromise(
          skuService.addChildSkusRelated(SkuRelationshipRequest.fromJS(values)),
        );

        queryClient.invalidateQueries([
          "skuService.getChildSkusRelated",
          sku.id!,
          values.skuRelationshipTypeId,
        ]);

        onSkuRelationshipCreated();
        onSkuUpdated && onSkuUpdated(sku.id!);

        return response;
      } catch (err: any) {
        console.log(err);
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        validate={validate}
        render={({ handleSubmit, submitting, submitError, values }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={1}>
                <Field name="skuRelationshipTypeId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectSkuRelationship
                          label={t({
                            id: "skuRelationshipTitle",
                          })}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                          required
                          {...rest}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                {values.skuRelationshipTypeId ? (
                  <Field name="childSkuId" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectSku
                            label={t({
                              id: "skuLabel",
                            })}
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                            required
                            {...rest}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>
                ) : (
                  <Grid item xs={12}>
                    <Typography sx={{ marginTop: "10px" }}>
                      <T id="selectRelationshipTypeMessage" />
                    </Typography>
                  </Grid>
                )}

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button disabled={submitting} onClick={onCancel}>
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting || !values.skuRelationshipTypeId}
              >
                {t({ id: "skuRelationshipSaveButtonLabel" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    );
  }, [
    styles.feedback,
    init,
    onCancel,
    onSkuRelationshipCreated,
    onSkuUpdated,
    queryClient,
    registerPromise,
    rest,
    sku.id,
    skuService,
    t,
  ]);
};

export default SkuRelationshipForm;
